import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import {
	AnonymousStudentProfileSynced,
	AnonymousStudentProfileUpdated,
	AnonymousStudentServiceReady,
	IAnonymousStudentService,
	StudentRepositoryStateType
} from '@studyportals/anonymous-student-interfaces';
import { StudentField } from '@studyportals/studentdomain';
import { ILocalizedContentService, LocalizedContentServiceReadyEvent } from '../interface';
import { CountryRepository } from '../../CountryRepository/CountryRepository';
import { LocalContent } from '../interface/LocalizedContent/dto/local-content';
import { LocalizationTargetType } from '../interface/LocalizedContent/enums/LocalizationTargetType';

/* eslint-disable @typescript-eslint/no-non-null-assertion, max-len */
export class LocalizedContentService
	implements
		ILocalizedContentService,
		ISubscriber<AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced>
{
	private anonymousStudent?: IAnonymousStudentService;
	private countryRepository: CountryRepository;

	constructor() {
		this.countryRepository = new CountryRepository();

		window.EventAggregationService.subscribeTo(AnonymousStudentServiceReady.EventType, this, true);
		window.EventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, this, false);
		window.EventAggregationService.subscribeTo(AnonymousStudentProfileSynced.EventType, this, false);
	}

	// eslint-disable-next-line max-len
	public async notify(
		event: AnonymousStudentServiceReady | AnonymousStudentProfileUpdated | AnonymousStudentProfileSynced
	): Promise<void> {
		if (event.eventType === AnonymousStudentServiceReady.EventType) {
			await this.handleAnonymousStudentReadyEvent(event as AnonymousStudentServiceReady);
		}

		if (event.eventType === AnonymousStudentProfileUpdated.EventType) {
			await this.handleAnonymousStudentUpdatedEvent(event as AnonymousStudentProfileUpdated);
		}

		if (event.eventType === AnonymousStudentProfileSynced.EventType) {
			await this.handleAnonymousStudentProfileSyncedEvent(event as AnonymousStudentProfileSynced);
		}
	}

	public async update(): Promise<void> {
		const data = await this.anonymousStudent!.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.NATIONALITY_COUNTRY_ISO
		]);

		if (
			data[StudentField.NATIONALITY_COUNTRY_ID] !== undefined &&
			data[StudentField.NATIONALITY_COUNTRY_ISO] !== undefined
		) {
			this.updateContent(data[StudentField.NATIONALITY_COUNTRY_ID]!, data[StudentField.NATIONALITY_COUNTRY_ISO]!);
		}
	}

	public async localizeContent<LocalContentType>(
		contents: LocalContent<LocalContentType>[]
	): Promise<LocalContent<LocalContentType> | null> {
		const data = await this.anonymousStudent!.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.NATIONALITY_COUNTRY_ISO
		]);

		if (
			data[StudentField.NATIONALITY_COUNTRY_ID] !== undefined &&
			data[StudentField.NATIONALITY_COUNTRY_ISO] !== undefined
		) {
			return this.getLocalizedContent(
				contents,
				data[StudentField.NATIONALITY_COUNTRY_ISO]!,
				data[StudentField.NATIONALITY_COUNTRY_ID]!
			);
		}

		return null;
	}

	private getLocalizedContent<LocalContentType>(
		contents: LocalContent<LocalContentType>[],
		nationality_country_iso: string,
		nationality_country_id: number
	): LocalContent<LocalContentType> | null {
		const isFromEEA = this.countryRepository.isCountryEEA(nationality_country_iso);

		const contentNational = contents.find((content) => content.target === LocalizationTargetType.NATIONAL);
		const contentEEA = contents.find((content) => content.target === LocalizationTargetType.EEA);
		const contentInternational = contents.find(
			(content) => content.target === LocalizationTargetType.INTERNATIONAL
		);
		const contentGeneral = contents.find((content) => content.target === LocalizationTargetType.GENERAL);
		const contentFallback = contents.find((content) => content.target === LocalizationTargetType.FALLBACK);

		if (
			contentNational &&
			(contentNational.countryIso === nationality_country_iso ||
				contentNational.countryId === nationality_country_id)
		) {
			return contentNational;
		} else if (isFromEEA && contentEEA) {
			return contentEEA;
		}

		return contentInternational || contentGeneral || contentFallback || null;
	}

	public async allLocalizedContent<LocalContentType>(
		contents: LocalContent<LocalContentType>[]
	): Promise<LocalContent<LocalContentType>[]> {
		const data = await this.anonymousStudent!.getStudentData([
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.NATIONALITY_COUNTRY_ISO
		]);

		if (
			data[StudentField.NATIONALITY_COUNTRY_ID] !== undefined &&
			data[StudentField.NATIONALITY_COUNTRY_ISO] !== undefined
		) {
			return this.getAllLocalizedContent(
				contents,
				data[StudentField.NATIONALITY_COUNTRY_ISO]!,
				data[StudentField.NATIONALITY_COUNTRY_ID]!
			);
		}

		return [];
	}

	private getAllLocalizedContent<LocalContentType>(
		contents: LocalContent<LocalContentType>[],
		nationality_country_iso: string,
		nationality_country_id: number
	): LocalContent<LocalContentType>[] {
		const isFromEEA = this.countryRepository.isCountryEEA(nationality_country_iso);

		const contentNational = contents.filter((content) => content.target === LocalizationTargetType.NATIONAL);
		const contentEEA = contents.filter((content) => content.target === LocalizationTargetType.EEA);
		const contentInternational = contents.filter(
			(content) => content.target === LocalizationTargetType.INTERNATIONAL
		);
		const contentGeneral = contents.filter((content) => content.target === LocalizationTargetType.GENERAL);
		const contentFallback = contents.filter((content) => content.target === LocalizationTargetType.FALLBACK);

		const contentNationalFiltered = contentNational.filter(
			(content) => content.countryIso === nationality_country_iso || content.countryId === nationality_country_id
		);
		if (contentNationalFiltered.length > 0) {
			return contentNationalFiltered;
		} else if (isFromEEA && contentEEA.length > 0) {
			return contentEEA;
		}
		if (contentInternational.length > 0) {
			return contentInternational;
		}
		if (contentGeneral.length > 0) {
			return contentGeneral;
		}
		if (contentFallback.length > 0) {
			return contentFallback;
		}
		return [];
	}

	private updateContent(nationality_country_id: number, nationality_country_iso: string): void {
		const parentContentContainers = document.getElementsByClassName(
			'js-LocalizedContent'
		) as HTMLCollectionOf<HTMLElement>;

		for (const parentContentContainer of parentContentContainers) {
			const contentNational = parentContentContainer.querySelector(
				`[data-target="${LocalizationTargetType.NATIONAL}"]`
			) as HTMLElement;
			const contentEEA = parentContentContainer.querySelector(
				`[data-target="${LocalizationTargetType.EEA}"]`
			) as HTMLElement;
			const contentInternational = parentContentContainer.querySelector(
				`[data-target="${LocalizationTargetType.INTERNATIONAL}"]`
			) as HTMLElement;
			const contentGeneral = parentContentContainer.querySelector(
				`[data-target="${LocalizationTargetType.GENERAL}"]`
			) as HTMLElement;
			const contentNotAvailable = parentContentContainer.querySelector('.js-notAvailable') as HTMLElement;

			const contentCountryIso = parentContentContainer.dataset.countryIso || null;
			const contentCountryId = Number(parentContentContainer.dataset.countryId) || null;

			const contents: LocalContent<HTMLElement>[] = [];
			if (contentNational)
				contents.push(
					new LocalContent(
						LocalizationTargetType.NATIONAL,
						contentNational,
						contentCountryIso,
						contentCountryId
					)
				);
			if (contentEEA)
				contents.push(
					new LocalContent(LocalizationTargetType.EEA, contentEEA, contentCountryIso, contentCountryId)
				);
			if (contentInternational)
				contents.push(
					new LocalContent(
						LocalizationTargetType.INTERNATIONAL,
						contentInternational,
						contentCountryIso,
						contentCountryId
					)
				);
			if (contentGeneral)
				contents.push(
					new LocalContent(
						LocalizationTargetType.GENERAL,
						contentGeneral,
						contentCountryIso,
						contentCountryId
					)
				);
			if (contentNotAvailable)
				contents.push(
					new LocalContent(
						LocalizationTargetType.FALLBACK,
						contentNotAvailable,
						contentCountryIso,
						contentCountryId
					)
				);

			const localizedContent = this.getLocalizedContent(
				contents,
				nationality_country_iso,
				nationality_country_id
			);

			if (localizedContent) {
				this.switchContent(parentContentContainer, localizedContent.content);
			}
		}
	}

	private async handleAnonymousStudentReadyEvent(event: AnonymousStudentServiceReady): Promise<void> {
		this.anonymousStudent = event.anonymousStudentService;
		await this.update();
		this.fireReadyEvent();
	}

	private async handleAnonymousStudentUpdatedEvent(event: AnonymousStudentProfileUpdated): Promise<void> {
		const changes = event.changes;
		if (changes[StudentField.NATIONALITY_COUNTRY_ID] || changes[StudentField.NATIONALITY_COUNTRY_ISO]) {
			await this.update();
		}
	}

	private async handleAnonymousStudentProfileSyncedEvent(event: AnonymousStudentProfileSynced): Promise<void> {
		if (event.state === StudentRepositoryStateType.ONLINE) {
			await this.update();
		}
	}

	private switchContent(parentContentContainer: HTMLElement, activeLocalizedContent: HTMLElement): void {
		const allContentContainers = parentContentContainer.querySelectorAll('[data-target]');

		parentContentContainer.querySelector('.js-notAvailable')!.classList.add('Hidden');
		for (const contentContainer of allContentContainers) {
			contentContainer.classList.add('Hidden');
		}

		activeLocalizedContent.classList.remove('Hidden');
	}

	private fireReadyEvent(): void {
		const event = new LocalizedContentServiceReadyEvent(this);
		window.EventAggregationService.publishTo(LocalizedContentServiceReadyEvent.EventType, event);
	}
}
